import ReactDOM from 'react-dom';
import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { getSiteConfig, getConfigValue } from './site';
import schoolLogo from 'assets/img/blas-pascal-logo.png';

const QR_PREFIX = 'datofyqr://v1/';

const SCHOOL_NAME = 'BLAS PASCAL SCHOOL';
const SCHOOL_ADDRESS = 'Av. Manuel Rodríguez 895, Osorno';
const SCHOOL_PHONE = '64 220 1500';

// ─── School info ─────────────────────────────────────────────────────────────

export const getSchoolInfo = () => {
  try {
    const raw = getSiteConfig();
    if (!raw) return { name: SCHOOL_NAME, address: SCHOOL_ADDRESS, phone: SCHOOL_PHONE };
    const config = JSON.parse(raw);
    return {
      name: getConfigValue(config, 'SITE_NAME') || SCHOOL_NAME,
      address:
        getConfigValue(config, 'SITE_ADDRESS') ||
        getConfigValue(config, 'SCHOOL_ADDRESS') ||
        SCHOOL_ADDRESS,
      phone:
        getConfigValue(config, 'SITE_PHONE') ||
        getConfigValue(config, 'SCHOOL_PHONE') ||
        SCHOOL_PHONE,
    };
  } catch {
    return { name: SCHOOL_NAME, address: SCHOOL_ADDRESS, phone: SCHOOL_PHONE };
  }
};

// ─── Image helpers ────────────────────────────────────────────────────────────

const qrToDataUrl = (value) =>
  new Promise((resolve) => {
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;pointer-events:none;';
    document.body.appendChild(container);
    ReactDOM.render(
      React.createElement(QRCodeCanvas, { value, size: 600, level: 'M', includeMargin: false }),
      container,
      () => {
        // QRCodeCanvas draws inside useEffect (async). setTimeout(0) ensures the
        // effect has flushed before we read the canvas.
        setTimeout(() => {
          const canvas = container.querySelector('canvas');
          const url = canvas ? canvas.toDataURL('image/png') : null;
          ReactDOM.unmountComponentAtNode(container);
          document.body.removeChild(container);
          resolve(url);
        }, 0);
      }
    );
  });

// Carga una imagen soportando CORS (para avatares externos).
// Nunca lanza — si falla devuelve null para que el carnet se genere igual.
const loadImage = (src, cors = false) =>
  new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null); // silencioso
    img.src = src;
  });

// ─── Canvas helpers ───────────────────────────────────────────────────────────

// Splits `text` into lines that fit within `maxWidth` using the current ctx font.
// Returns at most `maxLines` lines; the last line gets an ellipsis only if
// the word itself is wider than maxWidth (single unbreakable word).
function wrapText(ctx, text, maxWidth, maxLines) {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    const test = current ? current + ' ' + word : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (maxLines && lines.length > maxLines) return lines.slice(0, maxLines);
  return lines;
}

// Corta una palabra única más ancha que maxWidth, agregando "…".
function truncateToWidth(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + '…').width > maxWidth) t = t.slice(0, -1);
  return t + '…';
}

// Ajusta nombre y apellido: usa el mayor tamaño de fuente (baseSize → minSize) con el
// que cada campo cabe en ≤ linesPerField líneas, sin superar maxTotalLines en total
// y cumpliendo `fits(size, lineCount)` si se entrega. Nunca descarta palabras: solo
// en el tamaño mínimo y con una palabra imposible de partir se agrega "…".
// Deja ctx.font en el tamaño elegido. Devuelve { size, lines }.
function fitNameLines(ctx, parts, maxWidth, { baseSize, minSize, linesPerField = 2, maxTotalLines = 4, fits }) {
  const texts = parts.filter(Boolean).map((p) => p.toUpperCase());
  for (let size = baseSize; size >= minSize; size -= 2) {
    ctx.font = 'bold ' + size + 'px Arial, sans-serif';
    const lines = [];
    let ok = true;
    for (const t of texts) {
      const wrapped = wrapText(ctx, t, maxWidth);
      if (wrapped.length > linesPerField || wrapped.some((l) => ctx.measureText(l).width > maxWidth)) {
        ok = false;
        break;
      }
      lines.push(...wrapped);
    }
    if (ok && lines.length <= maxTotalLines && (!fits || fits(size, lines.length))) {
      return { size, lines: lines.length ? lines : ['—'] };
    }
  }
  ctx.font = 'bold ' + minSize + 'px Arial, sans-serif';
  const lines = [];
  for (const t of texts) {
    const wrapped = wrapText(ctx, t, maxWidth).slice(0, linesPerField);
    if (wrapped.length) wrapped[wrapped.length - 1] = truncateToWidth(ctx, wrapped[wrapped.length - 1], maxWidth);
    lines.push(...wrapped.map((l) => truncateToWidth(ctx, l, maxWidth)));
  }
  return { size: minSize, lines: lines.length ? lines : ['—'] };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Dibuja una imagen con recorte "cover" (sin distorsión) en un rectángulo redondeado.
// Equivalente a object-fit:cover — recorta el centro de la imagen para rellenar w×h.
function drawRoundImage(ctx, img, x, y, w, h, r) {
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw    = w / scale;
  const sh    = h / scale;
  const sx    = (img.naturalWidth  - sw) / 2;
  const sy    = (img.naturalHeight - sh) / 2;
  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

// ─── Carnet PNG generator (horizontal) ────────────────────────────────────────
// Layout:  [QR izq] | [datos centro] | [foto der]
// Dimensiones: 680 × 420 px

const generateHorizontalCarnetPng = async (customer, qrValue, schoolInfo) => {
  // 600 DPI @ CR80 (86 × 54 mm): 86/25.4*600 ≈ 2031 px,  54/25.4*600 ≈ 1276 px
  const W = 2031;
  const H = 1276;
  const canvas = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── Layout constants (escalados ×3 respecto al canvas original 680×420) ──────
  const PAD      = 80;
  const GAP      = 42;
  const HEADER_H = 340;
  const FOOTER_H = 124;
  const BODY_Y   = HEADER_H + 40;
  const BODY_H   = H - FOOTER_H - BODY_Y;

  const QR_S    = 540;
  const PHOTO_W = 480;
  const PHOTO_H = 480;

  const QR_X    = PAD;
  const QR_Y    = BODY_Y + Math.floor((BODY_H - QR_S)    / 2);

  const PHOTO_X = W - PAD - PHOTO_W;
  const PHOTO_Y = BODY_Y + Math.floor((BODY_H - PHOTO_H) / 2);

  const INFO_X  = QR_X + QR_S + GAP;
  const INFO_W  = PHOTO_X - GAP - INFO_X;

  // ── Fondo general ────────────────────────────────────────────────────────────
  ctx.fillStyle = '#F7FAFC';
  ctx.fillRect(0, 0, W, H);

  // ── Header azul ──────────────────────────────────────────────────────────────
  ctx.fillStyle = '#2644cd';
  ctx.fillRect(0, 0, W, HEADER_H);
  ctx.fillStyle = '#2644cd';
  ctx.fillRect(0, 0, 24, HEADER_H);

  const schoolName = (schoolInfo?.name || 'Institución Educativa').toUpperCase();
  let nameFontSize = 91;
  ctx.font = 'bold ' + nameFontSize + 'px Arial, sans-serif';
  while (ctx.measureText(schoolName).width > W - 268 && nameFontSize > 55) {
    nameFontSize--;
    ctx.font = 'bold ' + nameFontSize + 'px Arial, sans-serif';
  }
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'left';
  ctx.fillText(schoolName, 86, 182, W - 220);

  const infoLine = [
    schoolInfo?.address,
    schoolInfo?.phone ? 'Tel: ' + schoolInfo.phone : '',
  ].filter(Boolean).join('  ·  ');
  if (infoLine) {
    ctx.font = '44px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(infoLine, 86, 281, W - 220);
  }
  // ── Logo del colegio (derecha del header) ────────────────────────────────────
  try {
    const logoImg = await loadImage(schoolLogo);
    if (logoImg) {
      const LOGO_S = 268;
      const logoX = W - PAD - LOGO_S;
      const logoY = (HEADER_H - LOGO_S) / 2;
      ctx.fillStyle = 'transparent';
      roundRect(ctx, logoX, logoY, LOGO_S, LOGO_S, 16);
      ctx.fill();
      drawRoundImage(ctx, logoImg, logoX + 8, logoY + 8, LOGO_S - 16, LOGO_S - 16, 10);
    }
  } catch { /* logo falla silenciosamente */ }

  // ── Zona QR (izquierda) ───────────────────────────────────────────────────────
  ctx.fillStyle = '#FFFFFF';
  ctx.shadowColor = 'rgba(0,0,0,0.10)';
  ctx.shadowBlur = 24;
  roundRect(ctx, QR_X, QR_Y, QR_S, QR_S, 30);
  ctx.fill();
  ctx.shadowBlur = 0;

  if (qrValue) {
    try {
      const qrDataUrl = await qrToDataUrl(qrValue);
      if (qrDataUrl) {
        const qrImg = await loadImage(qrDataUrl);
        if (qrImg) {
          const pad = 30;
          ctx.drawImage(qrImg, QR_X + pad, QR_Y + pad, QR_S - pad * 2, QR_S - pad * 2);
        }
      }
    } catch { /* QR falla silenciosamente */ }
  }

  // ── Zona foto (derecha) ───────────────────────────────────────────────────────
  const hasAvatar = (customer.meta_data || []).some(function(m) { return m.key === 'avatar_url' && m.value; });
  const apiBase   = (process.env.REACT_APP_SURVEY_API_URL || '').replace(/\/$/, '');
  const avatarSrc = hasAvatar && apiBase && customer.username
    ? apiBase + '/api/users/' + customer.username + '/avatar-image'
    : null;
  const photoImg  = await loadImage(avatarSrc, true);

  if (photoImg) {
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0,0,0,0.10)';
    ctx.shadowBlur = 24;
    roundRect(ctx, PHOTO_X - 12, PHOTO_Y - 12, PHOTO_W + 24, PHOTO_H + 24, 36);
    ctx.fill();
    ctx.shadowBlur = 0;
    drawRoundImage(ctx, photoImg, PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H, 24);
    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.lineWidth = 3;
    roundRect(ctx, PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H, 24);
    ctx.stroke();
  } else {
    ctx.fillStyle = '#E2E8F0';
    ctx.shadowColor = 'rgba(0,0,0,0.08)';
    ctx.shadowBlur = 18;
    roundRect(ctx, PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H, 30);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#A0AEC0';
    ctx.font = '144px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('?', PHOTO_X + PHOTO_W / 2, PHOTO_Y + PHOTO_H / 2 + 48);
    ctx.textAlign = 'left';
  }

  // ── Datos alumno (centro) — texto wrapeado, nunca distorsionado ──────────────
  ctx.save();
  ctx.beginPath();
  ctx.rect(INFO_X, BODY_Y, INFO_W, BODY_H);
  ctx.clip();

  const FIELD_SIZE  = 58;
  const GAP_NAME_FIELD = 96;

  const { size: NAME_SIZE, lines: nameLines } = fitNameLines(
    ctx,
    [customer.first_name, customer.last_name],
    INFO_W,
    {
      baseSize: 56,
      minSize: 36,
      maxTotalLines: 4,
      fits: (size, n) => n * (size + 18) + GAP_NAME_FIELD + FIELD_SIZE <= BODY_H,
    }
  );
  const NAME_LH = NAME_SIZE + 18;

  const blockH = nameLines.length * NAME_LH + GAP_NAME_FIELD + FIELD_SIZE;

  let drawY = BODY_Y + Math.round((BODY_H - blockH) / 2) + NAME_SIZE;

  ctx.font = 'bold ' + NAME_SIZE + 'px Arial, sans-serif';
  ctx.fillStyle = '#1A202C';
  ctx.textAlign = 'left';
  for (let i = 0; i < nameLines.length; i++) {
    ctx.fillText(nameLines[i], INFO_X, drawY);
    if (i < nameLines.length - 1) drawY += NAME_LH;
  }

  drawY += GAP_NAME_FIELD + FIELD_SIZE;
  ctx.font = 'bold ' + FIELD_SIZE + 'px Arial, sans-serif';
  ctx.fillStyle = '#718096';
  ctx.fillText('ESTUDIANTE', INFO_X, drawY);

  ctx.restore();

  // ── Footer ────────────────────────────────────────────────────────────────────
  ctx.fillStyle = '#EDF2F7';
  ctx.fillRect(0, H - FOOTER_H, W, FOOTER_H);
  ctx.strokeStyle = '#CBD5E0';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, H - FOOTER_H);
  ctx.lineTo(W, H - FOOTER_H);
  ctx.stroke();

  ctx.fillStyle = '#718096';
  ctx.font = '30px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('DOCUMENTO DE IDENTIFICACIÓN ESTUDIANTIL', W / 2, H - 42);

  return canvas.toDataURL('image/png');
};

// ─── Carnet PNG generator (vertical) ──────────────────────────────────────────
// Layout (de arriba a abajo):  header  →  foto  →  nombre/ESTUDIANTE  →  QR  →  footer
// Dimensiones: 1276 × 2031 px (misma área que el horizontal, en formato retrato)

const generateVerticalCarnetPng = async (customer, qrValue, schoolInfo) => {
  const W = 1276;
  const H = 2031;
  const canvas = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const HEADER_H = 540;
  const FOOTER_H = 150;
  const BODY_Y   = HEADER_H + 50;
  const BODY_H   = H - FOOTER_H - BODY_Y;

  // ── Fondo general ────────────────────────────────────────────────────────────
  ctx.fillStyle = '#F7FAFC';
  ctx.fillRect(0, 0, W, H);

  // ── Header azul ──────────────────────────────────────────────────────────────
  ctx.fillStyle = '#2644cd';
  ctx.fillRect(0, 0, W, HEADER_H);
  ctx.fillStyle = '#2644cd';
  ctx.fillRect(0, 0, W, 12);

  ctx.textAlign = 'center';

  // Logo centrado
  let logoBottom = 70;
  try {
    const logoImg = await loadImage(schoolLogo);
    if (logoImg) {
      const LOGO_S = 210;
      const logoX  = (W - LOGO_S) / 2;
      const logoY  = 70;
      ctx.fillStyle = 'transparent';
      roundRect(ctx, logoX, logoY, LOGO_S, LOGO_S, 16);
      ctx.fill();
      drawRoundImage(ctx, logoImg, logoX + 8, logoY + 8, LOGO_S - 16, LOGO_S - 16, 10);
      logoBottom = logoY + LOGO_S;
    }
  } catch { /* logo falla silenciosamente */ }

  const schoolName = (schoolInfo?.name || SCHOOL_NAME).toUpperCase();
  ctx.font = 'bold 103px Arial, sans-serif';
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(schoolName, W / 2, logoBottom + 129, W - 160);

  const infoLine = [
    schoolInfo?.address,
    schoolInfo?.phone ? 'Tel: ' + schoolInfo.phone : '',
  ].filter(Boolean).join('  ·  ');
  if (infoLine) {
    ctx.font = '76px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(infoLine, W / 2, logoBottom + 236, W - 160);
  }

  // ── Bloque central: foto → nombre/ESTUDIANTE → QR (centrado verticalmente) ──
  const AVATAR_S   = 460;
  const FIELD_SIZE  = 47;
  const QR_S        = 420;
  const GAP_AVATAR_NAME = 44;
  const GAP_NAME_FIELD  = 18;
  const GAP_FIELD_QR    = 56;
  const FIXED_H = AVATAR_S + GAP_AVATAR_NAME + GAP_NAME_FIELD + FIELD_SIZE + GAP_FIELD_QR + QR_S;

  const { size: NAME_SIZE, lines: nameLines } = fitNameLines(
    ctx,
    [customer.first_name, customer.last_name],
    W - 200,
    {
      baseSize: 75,
      minSize: 44,
      maxTotalLines: 4,
      fits: (size, n) => FIXED_H + n * (size + 16) <= BODY_H,
    }
  );
  const NAME_LH = NAME_SIZE + 16;
  const nameBlockH = nameLines.length * NAME_LH;

  const blockH =
    AVATAR_S + GAP_AVATAR_NAME + nameBlockH + GAP_NAME_FIELD + FIELD_SIZE + GAP_FIELD_QR + QR_S;

  let drawY = BODY_Y + Math.max(0, Math.round((BODY_H - blockH) / 2));

  // Foto
  const hasAvatar = (customer.meta_data || []).some(function(m) { return m.key === 'avatar_url' && m.value; });
  const apiBase   = (process.env.REACT_APP_SURVEY_API_URL || '').replace(/\/$/, '');
  const avatarSrc = hasAvatar && apiBase && customer.username
    ? apiBase + '/api/users/' + customer.username + '/avatar-image'
    : null;
  const photoImg  = await loadImage(avatarSrc, true);

  const avatarX = (W - AVATAR_S) / 2;
  if (photoImg) {
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0,0,0,0.10)';
    ctx.shadowBlur = 24;
    roundRect(ctx, avatarX - 12, drawY - 12, AVATAR_S + 24, AVATAR_S + 24, 32);
    ctx.fill();
    ctx.shadowBlur = 0;
    drawRoundImage(ctx, photoImg, avatarX, drawY, AVATAR_S, AVATAR_S, 24);
    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.lineWidth = 3;
    roundRect(ctx, avatarX, drawY, AVATAR_S, AVATAR_S, 24);
    ctx.stroke();
  } else {
    ctx.fillStyle = '#E2E8F0';
    ctx.shadowColor = 'rgba(0,0,0,0.08)';
    ctx.shadowBlur = 18;
    roundRect(ctx, avatarX, drawY, AVATAR_S, AVATAR_S, 24);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#A0AEC0';
    ctx.font = '128px Arial, sans-serif';
    ctx.fillText('?', W / 2, drawY + AVATAR_S / 2 + 44);
  }
  drawY += AVATAR_S + GAP_AVATAR_NAME;

  // Nombre
  ctx.font = 'bold ' + NAME_SIZE + 'px Arial, sans-serif';
  ctx.fillStyle = '#1A202C';
  drawY += NAME_SIZE;
  for (let i = 0; i < nameLines.length; i++) {
    ctx.fillText(nameLines[i], W / 2, drawY, W - 200);
    if (i < nameLines.length - 1) drawY += NAME_LH;
  }
  drawY += GAP_NAME_FIELD + FIELD_SIZE;

  // ESTUDIANTE
  ctx.font = 'bold ' + FIELD_SIZE + 'px Arial, sans-serif';
  ctx.fillStyle = '#718096';
  ctx.fillText('ESTUDIANTE', W / 2, drawY);
  drawY += GAP_FIELD_QR;

  // QR
  const qrX = (W - QR_S) / 2;
  ctx.fillStyle = '#FFFFFF';
  ctx.shadowColor = 'rgba(0,0,0,0.10)';
  ctx.shadowBlur = 24;
  roundRect(ctx, qrX, drawY, QR_S, QR_S, 28);
  ctx.fill();
  ctx.shadowBlur = 0;

  if (qrValue) {
    try {
      const qrDataUrl = await qrToDataUrl(qrValue);
      if (qrDataUrl) {
        const qrImg = await loadImage(qrDataUrl);
        if (qrImg) {
          const pad = 26;
          ctx.drawImage(qrImg, qrX + pad, drawY + pad, QR_S - pad * 2, QR_S - pad * 2);
        }
      }
    } catch { /* QR falla silenciosamente */ }
  }

  // ── Footer ────────────────────────────────────────────────────────────────────
  ctx.fillStyle = '#EDF2F7';
  ctx.fillRect(0, H - FOOTER_H, W, FOOTER_H);
  ctx.strokeStyle = '#CBD5E0';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, H - FOOTER_H);
  ctx.lineTo(W, H - FOOTER_H);
  ctx.stroke();

  ctx.fillStyle = '#718096';
  ctx.font = '26px Arial, sans-serif';
  ctx.textAlign = 'center';
  const footerLines = wrapText(ctx, 'DOCUMENTO DE IDENTIFICACIÓN ESTUDIANTIL', W - 110, 2);
  const footerLH = 34;
  let footerY = H - FOOTER_H / 2 - ((footerLines.length - 1) * footerLH) / 2 + 8;
  for (const line of footerLines) {
    ctx.fillText(line, W / 2, footerY);
    footerY += footerLH;
  }

  return canvas.toDataURL('image/png');
};

// ─── Dispatcher ────────────────────────────────────────────────────────────────

export const generateCarnetPng = async (customer, qrValue, schoolInfo, orientation = 'horizontal') => {
  return orientation === 'vertical'
    ? generateVerticalCarnetPng(customer, qrValue, schoolInfo)
    : generateHorizontalCarnetPng(customer, qrValue, schoolInfo);
};

// ─── Batch ZIP download ───────────────────────────────────────────────────────

function toSafeFilename(str) {
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase();
}

// Nombre de archivo único para ZIP y descarga individual: carnet_nombre_apellido_rut.png
export const getCarnetFilename = (c) =>
  'carnet_' + toSafeFilename((c.first_name || '') + '_' + (c.last_name || '') + '_' + (c.username || '')) + '.png';

export const buildCarnetZip = async (customers, schoolInfo, onProgress) => {
  const JSZip = (await import('jszip')).default;
  const { getOrCreateToken } = await import('../services/qrService');
  const zip  = new JSZip();
  let done   = 0;

  for (const c of customers) {
    try {
      const res      = await getOrCreateToken(c.id, c.username);
      const qrValue  = QR_PREFIX + res.data.token;
      const png      = await generateCarnetPng(c, qrValue, schoolInfo);
      const base64   = png.split(',')[1];
      zip.file(getCarnetFilename(c), base64, { base64: true });
    } catch (e) {
      console.error('Carnet error for ' + c.username + ':', e);
    }
    done++;
    if (onProgress) onProgress(done, customers.length);
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'carnets-estudiantiles.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export { QR_PREFIX };
